import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode, WheelEvent } from 'react'
import { useTranslation } from 'react-i18next'

interface InfiniteScrollTableProps {
  children: ReactNode
  loadedCount: number
  totalCount: number
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  onLoadMore: () => void
  ariaLabel: string
  testId: string
  empty?: boolean
}

const HEADER_HEIGHT = 40
const ROW_HEIGHT = 58
const MAX_VISIBLE_ROWS = 10
const EMPTY_HEIGHT = 160

export function InfiniteScrollTable({
  children,
  loadedCount,
  totalCount,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  ariaLabel,
  testId,
  empty = false,
}: InfiniteScrollTableProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [hasContentBelow, setHasContentBelow] = useState(false)

  const updateScrollHints = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    const { scrollTop, clientHeight, scrollHeight } = container
    setCanScrollUp(scrollTop > 8)
    setHasContentBelow(scrollTop + clientHeight < scrollHeight - 8)
  }, [])

  const maybeLoadMore = useCallback(() => {
    const container = scrollRef.current
    if (!container || !hasMore || loading || loadingMore) return
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight
    if (remaining < 96) onLoadMore()
  }, [hasMore, loading, loadingMore, onLoadMore])

  useEffect(() => {
    const frameId = window.requestAnimationFrame(updateScrollHints)
    window.addEventListener('resize', updateScrollHints)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', updateScrollHints)
    }
  }, [loadedCount, totalCount, updateScrollHints])

  function handleWheel(event: WheelEvent<HTMLDivElement>) {
    if (event.deltaY <= 0 || !hasMore || loading || loadingMore) return
    const container = scrollRef.current
    if (!container) return
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight
    if (remaining <= 8) onLoadMore()
  }

  const showDownHint = hasContentBelow || hasMore
  const statusText = loadingMore
    ? t('members.pagination.loadingMore')
    : hasMore
      ? t('members.pagination.scrollForMore')
      : t('members.pagination.end')
  const visibleRows = Math.min(loadedCount, MAX_VISIBLE_ROWS)
  const tableHeight = empty || visibleRows === 0
    ? EMPTY_HEIGHT
    : HEADER_HEIGHT + visibleRows * ROW_HEIGHT

  return (
    <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--border)' }}>
      <div className="relative">
        <div
          className={`pointer-events-none absolute left-1/2 top-11 z-20 -translate-x-1/2 transition-all duration-300 ${
            canScrollUp ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
          }`}
          style={{ animation: canScrollUp ? 'morphScrollHint 1.8s ease-in-out infinite' : 'none' }}
          aria-hidden="true"
          data-testid={`${testId}-up-hint`}
        >
          <div className="rounded-full border px-3 py-1 text-[10px]" style={scrollHintStyle}>↑</div>
        </div>

        <div
          className={`pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 transition-all duration-300 ${
            showDownHint ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          }`}
          style={{ animation: showDownHint ? 'morphScrollHint 1.8s ease-in-out infinite' : 'none' }}
          aria-hidden="true"
          data-testid={`${testId}-down-hint`}
        >
          <div className="rounded-full border px-3 py-1 text-[10px]" style={scrollHintStyle}>↓</div>
        </div>

        <div
          ref={scrollRef}
          className="morph-scrollbar overflow-auto"
          style={{ height: tableHeight }}
          onScroll={() => {
            updateScrollHints()
            maybeLoadMore()
          }}
          onWheel={handleWheel}
          aria-label={ariaLabel}
          aria-busy={loading || loadingMore}
          data-testid={`${testId}-scroll`}
        >
          {children}
        </div>
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-[11px]"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)' }}
        data-testid={`${testId}-footer`}
      >
        <span>{t('members.pagination.loaded', { loaded: loadedCount, total: totalCount })}</span>
        <span style={{ color: hasMore ? 'var(--accent)' : 'var(--text-dim)' }}>{statusText}</span>
      </div>
    </div>
  )
}

const scrollHintStyle = {
  borderColor: 'rgba(99,102,241,.24)',
  background: 'rgba(15,23,42,.78)',
  color: '#C7D2FE',
  backdropFilter: 'blur(8px)',
}
